import React, { useState, useEffect } from 'react';
import apiService from '../../../services/api.service';
import './BannerManager.css';

const BannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    file: null,
    preview: null
  });
  const [editBanner, setEditBanner] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await apiService.getBanners();
      if (response.data.success) {
        setBanners(response.data.data || []);
      } else {
        setBanners([]);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
      setBanners([]);
      setErrorMessage('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          file: file,
          preview: reader.result
        }));
        setErrorMessage('');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      setErrorMessage('Please select an image file');
      return;
    }

    if (!formData.title.trim()) {
      setErrorMessage('Please enter a title');
      return;
    }

    try {
      setUploading(true);
      setErrorMessage('');

      const uploadFormData = new FormData();
      uploadFormData.append('image', formData.file);
      uploadFormData.append('title', formData.title);
      uploadFormData.append('status', '1');

      // Upload to API using apiService
      const response = await apiService.uploadBanner(uploadFormData);

      if (response.data.success) {
        setSuccessMessage('Banner uploaded successfully!');
        setFormData({ title: '', file: null, preview: null });
        setTimeout(() => {
          setSuccessMessage('');
          fetchBanners();
        }, 2000);
      } else {
        setErrorMessage(response.data.error || 'Failed to upload banner');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setErrorMessage(error.response?.data?.error || 'Error uploading banner. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      const response = await apiService.deleteBanner(bannerId);
      if (response.data.success) {
        setSuccessMessage('Banner deleted successfully!');
        setTimeout(() => {
          setSuccessMessage('');
          fetchBanners();
        }, 2000);
      } else {
        setErrorMessage('Failed to delete banner');
      }
    } catch (error) {
      console.error('Delete error:', error);
      setErrorMessage('Error deleting banner');
    }
  };

  const handleToggleStatus = async (bannerId, currentStatus) => {
    try {
      const newStatus = currentStatus === '1' ? '0' : '1';
      const response = await apiService.updateBannerStatus(bannerId, newStatus);
      if (response.data.success) {
        fetchBanners();
      }
    } catch (error) {
      console.error('Status update error:', error);
      setErrorMessage('Error updating banner status');
    }
  };

  return (
    <div className="banner-manager">
      <div className="banner-header">
        <h2>Banner Image Management</h2>
        <p>Upload and manage images for the home page carousel</p>
      </div>

      {successMessage && <div className="alert alert-success">{successMessage}</div>}
      {errorMessage && <div className="alert alert-error">{errorMessage}</div>}

      <div className="banner-container">
        {/* Upload Form */}
        <div className="upload-section">
          <div className="upload-card">
            <h3>Upload New Banner</h3>
            <form onSubmit={handleUpload} className="upload-form">
              <div className="form-group">
                <label htmlFor="title">Banner Title</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., SSVASS Exhibition 2025"
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label htmlFor="image">Image File</label>
                <div className="file-upload" onClick={() => fileInputRef.current?.click()}>
                  <input
                    type="file"
                    id="image"
                    name="image"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  <div className="upload-hint">
                    <i className="flaticon-upload"></i>
                    <p>Click to select image or drag and drop</p>
                    <small>Recommended: 1920x600px, Max: 5MB</small>
                  </div>
                </div>
              </div>

              {formData.preview && (
                <div className="preview-container">
                  <label>Preview</label>
                  <img src={formData.preview} alt="Preview" className="preview-image" />
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, preview: null, file: null }))}
                    className="btn-remove-preview"
                  >
                    Remove
                  </button>
                </div>
              )}

              <button
                type="submit"
                className="btn-upload"
                disabled={uploading || !formData.file}
              >
                {uploading ? 'Uploading...' : 'Upload Banner'}
              </button>
            </form>
          </div>
        </div>

        {/* Banners List */}
        <div className="banners-list">
          <h3>Current Banners</h3>
          
          {loading ? (
            <div className="loading-state">
              <p>Loading banners...</p>
            </div>
          ) : banners.length === 0 ? (
            <div className="empty-state">
              <i className="flaticon-image"></i>
              <p>No banners uploaded yet. Upload your first banner above!</p>
            </div>
          ) : (
            <div className="banners-grid">
              {banners.map((banner) => (
                <div key={banner.id} className="banner-item">
                  <div className="banner-image">
                    <img src={banner.image} alt={banner.title} />
                    <div className="banner-overlay">
                      <div className="banner-actions">
                        <button
                          className={`btn-status ${banner.status === '1' ? 'active' : 'inactive'}`}
                          onClick={() => handleToggleStatus(banner.id, banner.status)}
                          title={banner.status === '1' ? 'Deactivate' : 'Activate'}
                        >
                          <i className={`flaticon-${banner.status === '1' ? 'eye' : 'eye-off'}`}></i>
                          {banner.status === '1' ? 'Active' : 'Inactive'}
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDeleteBanner(banner.id)}
                          title="Delete"
                        >
                          <i className="flaticon-trash"></i>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="banner-info">
                    <h4>{banner.title}</h4>
                    <p className="upload-date">
                      Uploaded: {new Date(banner.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BannerManager;
